import { useState } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';

import {
  ReadingCacheType,
  useReadAPIWithServices,
  useWriteAPIWithServices,
} from 'api/useAPIWithServices';
import ROUTES from 'app/routes/paths';
import { UiWithReleaseOrDraftProcessId } from 'app/routes/uiPathParams';
import { FileReadWriteState } from 'components/organisms/fileViewModal/types';
import { LOGS_SOURCES } from 'components/organisms/logview/config';
import { Tab } from 'components/organisms/tabs';
import UiUrlParams from 'constants/uiUrlParams';
import { useAppServices } from 'container/depContext';
import { isApiError } from 'container/models';
import {
  AutomatedIndexingTask,
  SearchAutomatedIndexingTasksRequest,
} from 'features/automatedIndexing/types';
import { Task } from 'features/shared/task/types';
import { useApplicationErrorHandling } from 'hooks/useApplicationErrorHandling';
import useModal from 'hooks/useModal';
import useToaster from 'hooks/useToaster';
import { isAutomatedIndexingTask } from 'pages/automatedIndexingDetails/index';
import { ApplicationError } from 'types/errorHandling';

import { TAB_DETAILS, TABLE_MENU_ACCESSOR, TABS } from './config';

const useAutomatedIndexingDetailsLogic = () => {
  // #region DATA
  const { goBack, push } = useHistory();
  const { addToaster } = useToaster();
  const handleAppErr = useApplicationErrorHandling();

  const [currentTab, setCurrentTab] = useState<Tab>(TABS[0]);
  const [taskToDelete, setTaskToDelete] = useState<
    AutomatedIndexingTask | undefined
  >(undefined);

  const [showDeleteTaskModal, handleShowDeleteTaskModal] = useModal();

  const { getAutomatedIndexingRepo, getProcessRepo } = useAppServices();
  const {
    searchAutomatedIndexingTasks,
    resumeTrainingTask,
    uploadFile,
    deleteAutomatedIndexingTask,
  } = getAutomatedIndexingRepo();

  const { releaseOrDraftProcessId } =
    useParams<UiWithReleaseOrDraftProcessId>();

  const { getProcessDraft } = getProcessRepo();

  const { data: processDraft, isLoading: isProcessDraftLoading } =
    useReadAPIWithServices(
      [getProcessDraft.name, releaseOrDraftProcessId],
      () => getProcessDraft(releaseOrDraftProcessId),
      {
        cacheType: ReadingCacheType.ExecutionDraftDependency,
      }
    );

  const {
    trigger: createAutomatedIndexingTaskTrigger,
    isExecuting: isCreateAutomatedIndexingTaskExecuting,
  } = useWriteAPIWithServices(
    [uploadFile.name, processDraft],
    (file: File) => uploadFile(processDraft!, file),
    {
      onSuccess: () => {
        addToaster({ message: 'File uploaded successfully' });
      },
    }
  );

  const { trigger: deleteTaskTrigger, isExecuting: isDeleteTaskExecuting } =
    useWriteAPIWithServices<AutomatedIndexingTask, void>(
      [deleteAutomatedIndexingTask.name],
      (taskToRetry) => deleteAutomatedIndexingTask(taskToRetry)
    );

  const automatedIndexingTasksQuery =
    ((): SearchAutomatedIndexingTasksRequest => {
      switch (currentTab.value) {
        case TAB_DETAILS.NEW_ORDER.value:
          return {
            filter: {
              operator: 'OR',
              items: [
                {
                  field: 'status',
                  operation: 'equal',
                  value: 'in_ocr',
                },
                {
                  field: 'status',
                  operation: 'equal',
                  value: 'ocr_ready',
                },
              ],
            },
          };
        case TAB_DETAILS.TRAINING_READY.value:
          return {
            filter: {
              operator: 'OR',
              items: [
                {
                  field: 'status',
                  operation: 'equal',
                  value: 'ready_for_training',
                },
              ],
            },
          };
        case TAB_DETAILS.ERRORS.value:
          return {
            filter: {
              operator: 'OR',
              items: [
                {
                  field: 'status',
                  operation: 'equal',
                  value: 'failed',
                },
              ],
            },
          };
        default:
          return {};
      }
    })();

  const { data: automatedIndexingTasks, mutate: clearAutomatedIndexingTasks } =
    useReadAPIWithServices(
      [
        searchAutomatedIndexingTasks.name,
        processDraft,
        automatedIndexingTasksQuery,
      ],
      () =>
        searchAutomatedIndexingTasks(
          processDraft!,
          automatedIndexingTasksQuery,
          false
        ),
      {
        cacheType: ReadingCacheType.ExecutionMainEntity,
      }
    );

  const {
    trigger: resumeTrainingTaskTrigger,
    isExecuting: isResumeTrainingTaskExecuting,
  } = useWriteAPIWithServices<AutomatedIndexingTask, void>(
    [resumeTrainingTask.name],
    (task) => resumeTrainingTask(task)
  );

  const groups = ((): [number, AutomatedIndexingTask[]][] =>
    Array.from(
      // group tasks by date using midnight on the day as a group key
      Map.groupBy(
        (automatedIndexingTasks as AutomatedIndexingTask[]) ?? [],
        (item) => {
          const date = new Date(item.updatedAt);
          date.setHours(0, 0, 0, 0); // set date to local midnight
          return date.valueOf();
        }
      ).entries()
    )
      // ensure the tasks within each group are ordered
      .map(([key, tasks]): [number, Array<AutomatedIndexingTask>] => [
        key,
        tasks.toSorted(
          ({ updatedAt: a }, { updatedAt: b }) =>
            new Date(a).valueOf() - new Date(b).valueOf()
        ),
      ])
      // ensure the groups are ordered
      .toSorted(([a], [b]) => b - a))();

  const [fileViewTask, setFileViewTask] = useState<AutomatedIndexingTask>();

  const fileViewData = fileViewTask
    ? [
        {
          fileUrl: fileViewTask._links?.openFile.href as string,
          id: 'key',
          readWriteState: FileReadWriteState.ReadOnly,
        },
      ]
    : [];
  const isFileViewModalVisible = !!fileViewTask;

  const anyFetching =
    isCreateAutomatedIndexingTaskExecuting ||
    isProcessDraftLoading ||
    isResumeTrainingTaskExecuting ||
    isDeleteTaskExecuting;
  // endregion

  // #region CALLBACKS
  const handleAutomatedIndexingLogsClick = () => {
    push(
      ROUTES.process.systemLogs._({
        [UiUrlParams.draftProcessId]: releaseOrDraftProcessId,
        [UiUrlParams.source]: LOGS_SOURCES.AUTOMATED_INDEXING,
      })
    );
  };

  const handleCloseFileViewModal = () => {
    setFileViewTask(undefined);
  };

  const handleTabClick = (tab: Tab) => {
    setCurrentTab(tab);
  };

  const handleGoToLogs = (task: AutomatedIndexingTask) => {
    push(
      ROUTES.process.systemLogs._({
        [UiUrlParams.draftProcessId]: processDraft!.id,
        [UiUrlParams.source]: LOGS_SOURCES.AUTOMATED_INDEXING,
      }) +
        '?' +
        new URLSearchParams({
          name: `taskid:${task.id}`,
        }).toString()
    );
  };

  const handleViewFile = (task: AutomatedIndexingTask) => {
    setFileViewTask(task);
  };

  const handleMenuItemClick = (accessor: TABLE_MENU_ACCESSOR, item: Task) => {
    if (!isAutomatedIndexingTask(item)) {
      handleAppErr(
        new ApplicationError(
          'Not an Automated Indexing task',
          'Not an Automated Indexing task'
        )
      );
      return;
    }
    switch (accessor) {
      case TABLE_MENU_ACCESSOR.DELETE_TASK: {
        handleShowDeleteTaskModal();
        setTaskToDelete(item);
        break;
      }
      default:
        break;
    }
  };

  const handleDeleteTaskModalCancelClick = () => {
    setTaskToDelete(undefined);
    handleShowDeleteTaskModal();
  };

  const handleDeleteTaskModalSubmitClick = () => {
    if (!taskToDelete) {
      handleAppErr(
        new ApplicationError(
          'No task found to delete',
          'No task found to delete'
        )
      );
      return;
    }
    deleteTaskTrigger(taskToDelete).then((result) => {
      if (!isApiError(result)) {
        clearAutomatedIndexingTasks().then(); // do nothing with the result
        addToaster({
          type: 'info',
          message: 'Task has been successfully deleted',
        });
        setTaskToDelete(undefined);
        handleShowDeleteTaskModal();
      }
    });
  };

  const handleResumeTrainingTask = (task: AutomatedIndexingTask) => {
    resumeTrainingTaskTrigger(task).then(() => {
      addToaster({
        type: 'info',
        message: 'Successfully resumed task execution',
      });
      clearAutomatedIndexingTasks().then(); // do nothing with the result
    });
  };
  // endregion

  return {
    createAutomatedIndexingTaskTrigger,
    anyFetching,
    handleAutomatedIndexingLogsClick,
    fileViewData,
    groups,
    handleCloseFileViewModal,
    handleTabClick,
    isFileViewModalVisible,
    handleViewFile,
    handleGoToLogs,
    goBack,
    currentTab,
    handleMenuItemClick,
    handleDeleteTaskModalCancelClick,
    handleDeleteTaskModalSubmitClick,
    showDeleteTaskModal,
    handleResumeTrainingTask,
  };
};
export default useAutomatedIndexingDetailsLogic;
